import {FilterType, IObjectSearchParams, template as tpl, viewModel as ObjectSearch} from "../search";
import {_} from 'lodash';
import {IObjectData, IResponse, ISearchObjectParams, TRouteId} from "../../../index";
import {storeObservable} from "../../../util/sessionstore";
import {ObjectTypeId} from "../../../constants";
import {IDialogOptions} from "../../dialog/index";
import {getRouteDefinition} from "../../../constants/RouteDefinitions";
import {IDialogOpt} from "../../dialog/manager";

export let template: string = tpl;

export interface IDialogPickObjectParams extends IObjectSearchParams {
    __dialog?: IDialogOpt
    dialog?: IDialogOptions
    /** Object ids omitted from search results (client-side, per page). */
    excludeObjectIds?: number[]
    objecttypeid: ObjectTypeId
    onObjectSelected: (obj: IObjectData) => void,
    orgnr?: string
    selectedObject?: KnockoutObservable<IObjectData>
}

export class viewModel extends ObjectSearch {

    static readonly ObjectTypeId2ComponentName: Partial<Record<ObjectTypeId, TRouteId>> = {
        [ObjectTypeId.User]: 'usersearch',
        [ObjectTypeId.Department]: 'departmentsearch',
        [ObjectTypeId.Group]: 'groupsearch',
        [ObjectTypeId.FormData]: 'formdatasearch',
        [ObjectTypeId.ExternalContact]: 'externalcontactsearch',
    }

    /** Caps auto-page skips when every row on a page is already selected. */
    private emptyExcludePageSkips = 0;

    constructor(protected override params: IDialogPickObjectParams) {
        const routeId = viewModel.ObjectTypeId2ComponentName[params.objecttypeid];
        super(_.defaults(params, {
            isSelectable: false,
            __routeDef: _.defaults({showHeading: false}, getRouteDefinition(routeId!) ?? {}),
            __objectTypeId: params.objecttypeid,
        }));
    }

    protected override getTableKeys(): string[] {
        const ret = storeObservable(`search.object.syncfields${this.__objectTypeId}` as `search.object.syncfields${ObjectTypeId}`)()
            .filter((key: string) => this.showTableColumn(key))
        if (this.params.syncfieldNames?.length) {
            ret.push(...this.params.syncfieldNames)
        }
        return _.uniq(ret)
    }

    override isFilterAvailable(filterType: FilterType) {
        switch (filterType) {
            case FilterType.Deleted:
                return false;
            default:
                return super.isFilterAvailable(filterType);
        }
    }

    override onSuccess(response: IResponse) {
        const excludeIds = this.params.excludeObjectIds;
        if (excludeIds?.length && Array.isArray(response.data)) {
            const original = response.data as IObjectData[];
            const exclude = new Set(excludeIds);
            const data = original.filter(o => !exclude.has(o.id));
            const removed = original.length - data.length;
            const totalresults = Math.max(0, (response.totalresults ?? 0) - removed);

            // Empty filtered page would hit onSuccess's empty branch (wipe pager) while other
            // pages may still have pickable rows — skip to an adjacent page instead.
            if (!data.length && removed && this.emptyExcludePageSkips < 50) {
                const pageSize = this.searchresultsprpage() || original.length || 1;
                const originalTotal = response.totalresults ?? 0;
                const pagenr = response.pagenr ?? 0;
                if ((pagenr + 1) * pageSize < originalTotal) {
                    this.emptyExcludePageSkips++;
                    this.searchpagenr(pagenr + 1);
                    return;
                }
                if (pagenr > 0) {
                    this.emptyExcludePageSkips++;
                    this.searchpagenr(pagenr - 1);
                    return;
                }
            }

            this.emptyExcludePageSkips = 0;
            response = {
                ...response,
                data,
                totalresults: data.length ? totalresults : 0,
            };
        }
        super.onSuccess(response);
    }

    protected override onRowClick(item: IObjectData) {
        if (this.params.fnValidateRow && !this.params.fnValidateRow(item)) {
            return;
        }
        this.params.selectedObject?.(item);
        this.params.onObjectSelected?.(item);
        this.params.dialog?.doClose?.();
        this.params.__dialog?.close?.();
    }

    protected override getParams(...parameters: Partial<ISearchObjectParams>[]) {
        return super.getParams(
            ...(this.params.orgnr != null ? [{orgnr: this.params.orgnr}] : []),
            ...parameters,
        )
    }
}
